import React, { useEffect, useRef, useState, useCallback } from "react";
import Cookies from "js-cookie";
import { Link, useNavigate } from "react-router-dom";
import wheelImg from "../assets/wheel.png";
import arrowImg from "../assets/arrow.png";
import { checkEligibility, playSpin } from "../api/hitapi";

// Web Audio API Sound Controller for arcade-quality audio effects
class SoundController {
  constructor() {
    this.ctx = null;
    this.muted = false;
  }
  init() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this.ctx.state === "suspended") {
      this.ctx.resume();
    }
  }
  playTick() {
    if (this.muted) return;
    this.init();
    try {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.type = "triangle";
      osc.frequency.setValueAtTime(600, this.ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(120, this.ctx.currentTime + 0.04);
      gain.gain.setValueAtTime(0.12, this.ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.04);
      osc.start();
      osc.stop(this.ctx.currentTime + 0.04);
    } catch (e) {
      console.warn("Audio play blocked or unsupported:", e);
    }
  }
  playWin() {
    if (this.muted) return;
    this.init();
    try {
      const now = this.ctx.currentTime;
      const playTone = (freq, startTime, duration) => {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.connect(gain);
        gain.connect(this.ctx.destination);
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, startTime);
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(0.2, startTime + 0.04);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
        osc.start(startTime);
        osc.stop(startTime + duration);
      };
      playTone(523.25, now, 0.12);
      playTone(659.25, now + 0.1, 0.12);
      playTone(783.99, now + 0.2, 0.12);
      playTone(1046.50, now + 0.3, 0.5);
    } catch (e) {
      console.warn("Audio play blocked or unsupported:", e);
    }
  }
  playLose() {
    if (this.muted) return;
    this.init();
    try {
      const now = this.ctx.currentTime;
      const playTone = (freq, startTime, duration) => {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.connect(gain);
        gain.connect(this.ctx.destination);
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(freq, startTime);
        osc.frequency.linearRampToValueAtTime(freq - 80, startTime + duration);
        gain.gain.setValueAtTime(0.15, startTime);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
        osc.start(startTime);
        osc.stop(startTime + duration);
      };
      playTone(330, now, 0.15);
      playTone(220, now + 0.18, 0.35);
    } catch (e) {
      console.warn("Audio play blocked or unsupported:", e);
    }
  }
}

const soundCtrl = new SoundController();

// Wheel wedges config matching user request and screenshot
const wedges = [
  { label: "5,000", subLabel: "COINS", color: "#A30000", textColor: "#FFFFFF", icon: "🪙", value: 5000, isWin: true },
  { label: "10,000", subLabel: "COINS", color: "#0A1931", textColor: "#FFFFFF", icon: "🪙", value: 10000, isWin: true },
  { label: "30,000", subLabel: "COINS", color: "#064E3B", textColor: "#FFFFFF", icon: "💼", value: 30000, isWin: true },
  { label: "JACKPOT", subLabel: "", color: "#92400E", textColor: "#FFFFFF", icon: "👑", value: 100000, isWin: true },
  { label: "BETTER LUCK", subLabel: "NEXT TIME", color: "#1E1B4B", textColor: "#FFFFFF", icon: "😢", value: 0, isWin: false },
  { label: "50,000", subLabel: "COINS", color: "#0F766E", textColor: "#FFFFFF", icon: "💰", value: 50000, isWin: true },
  { label: "20,000", subLabel: "COINS", color: "#4C1D95", textColor: "#FFFFFF", icon: "💎", value: 20000, isWin: true },
  { label: "BETTER LUCK", subLabel: "NEXT TIME", color: "#1E1B4B", textColor: "#FFFFFF", icon: "😢", value: 0, isWin: false }
];

// 3D-like falling gold coin class
class Coin {
  constructor(canvasWidth, canvasHeight, isExplosion = false) {
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.isExplosion = isExplosion;
    
    this.x = isExplosion ? canvasWidth / 2 + (Math.random() - 0.5) * 80 : Math.random() * canvasWidth;
    this.y = isExplosion ? canvasHeight / 2 + (Math.random() - 0.5) * 80 : -50 - Math.random() * 300;
    this.radius = isExplosion ? Math.random() * 7 + 5 : Math.random() * 11 + 6;
    
    // Physics velocities
    if (isExplosion) {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 12 + 4;
      this.vx = Math.cos(angle) * speed;
      this.vy = Math.sin(angle) * speed - Math.random() * 5;
      this.gravity = 0.3;
    } else {
      this.vx = (Math.random() - 0.5) * 1.2;
      this.vy = Math.random() * 2.2 + 1.6;
      this.gravity = 0;
    }
    
    this.rotation = Math.random() * Math.PI * 2;
    this.rotationSpeed = (Math.random() - 0.5) * 0.08;
    this.flipAngle = Math.random() * Math.PI;
    this.flipSpeed = Math.random() * 0.06 + 0.04;
    this.opacity = Math.random() * 0.3 + 0.7;
    this.scale = isExplosion ? Math.random() * 0.4 + 0.6 : Math.random() * 0.6 + 0.4;
  }

  update(canvasWidth, canvasHeight) {
    if (this.isExplosion) {
      this.vy += this.gravity;
      this.opacity -= 0.008;
    }
    
    this.x += this.vx;
    this.y += this.vy;
    this.rotation += this.rotationSpeed;
    this.flipAngle += this.flipSpeed;
    
    // Recycle standard rain coins
    if (!this.isExplosion && this.y > canvasHeight + 30) {
      this.y = -30;
      this.x = Math.random() * canvasWidth;
      this.vy = Math.random() * 2.2 + 1.6;
      this.vx = (Math.random() - 0.5) * 1.2;
    }
  }

  draw(ctx) {
    if (this.opacity <= 0) return;
    
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.scale(Math.cos(this.flipAngle) * this.scale, 1 * this.scale);
    ctx.rotate(this.rotation);
    ctx.globalAlpha = this.opacity;
    
    // Depth of field blur for distant items
    if (this.scale < 0.55 && !this.isExplosion) {
      ctx.filter = "blur(1px)";
    }

    // Gold coin base gradient
    ctx.beginPath();
    ctx.arc(0, 0, this.radius, 0, Math.PI * 2);
    const grad = ctx.createRadialGradient(-this.radius/3, -this.radius/3, this.radius/10, 0, 0, this.radius);
    grad.addColorStop(0, "#FFE875"); // shiny yellow
    grad.addColorStop(0.5, "#F39C12"); // warm gold
    grad.addColorStop(1, "#B7950B"); // dark bronze outline
    ctx.fillStyle = grad;
    ctx.fill();

    // Embossed rim border
    ctx.lineWidth = this.radius * 0.12;
    ctx.strokeStyle = "#9A7D0A";
    ctx.stroke();

    // Inner ring detail
    ctx.beginPath();
    ctx.arc(0, 0, this.radius * 0.65, 0, Math.PI * 2);
    ctx.lineWidth = this.radius * 0.05;
    ctx.strokeStyle = "#F5B041";
    ctx.stroke();

    // Center emblem (star/symbol)
    ctx.font = `bold ${this.radius * 0.75}px sans-serif`;
    ctx.fillStyle = "#7D6608";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("$", 0, 0);

    ctx.restore();
    ctx.filter = "none";
  }
}

// Maps backend rewardType string → front-end wedge index
// Front-end wedges: 0=5000, 1=10000, 2=30000, 3=JACKPOT, 4=LOSE, 5=50000, 6=20000, 7=LOSE
// const REWARD_TYPE_TO_WEDGE_INDEX = {
//   "5000":    0,
//   "10000":   1,
//   "20000":   6,
//   "50000":   5,
//   "80000":   3,   // no 80k segment on wheel image → map to JACKPOT visual
//   "JACKPOT": 3,
//   "100000":  3,
//   "LOSE":    null, // randomly 4 or 7 (both are "Better Luck" segments)
// };

// Backend rewardType → Frontend wedge index (fixed mapping)

// Backend rewardType → Front-end wedge index (fixed mapping)

// const REWARD_TYPE_TO_WEDGE_INDEX = {
//   "5000": 0,     // Top
//   "10000": 1,    // Top-Right
//   "80000": 2,    // Right
//   "JACKPOT": 3,   // Bottom-Right
//   "100000": 3,   // also JACKPOT

//   "LOSE": 4,     // Bottom (fixed, no randomness)

//   "50000": 5,    // Bottom-Left
//   "20000": 6,    // Left

//   // second LOSE position (Top-Left)
//   "LOSE_2": 7,
// };


// const REWARD_TYPE_TO_WEDGE_INDEX={
//   0: 4, // LOSE -> Bottom
//   1: 0, // 5000 -> Top
//   2: 1, // 10000 -> Top-Right
//   3: 6, // 20000 -> Left
//   4: 5, // 50000 -> Bottom-Left
//   5: 2, // 80000 -> Right
//   6: 3, // JACKPOT -> Bottom-Right
//   7: 7, // LOSE -> Top-Left
// };

const REWARD_TYPE_TO_WEDGE_INDEX= {
  "5000": 0,      // Top
  "10000": 1,     // Top-Right
  "20000": 6,     // Left
  "50000": 5,     // Bottom-Left
  "80000": 2,     // Right
  "JACKPOT": 3,   // Bottom-Right
  "LOSE": 4,      // Bottom
};

const GameScene = () => {
  const navigate = useNavigate();
  const rainCanvasRef = useRef(null);
  const pointerRef = useRef(null);
  const wheelContainerRef = useRef(null);

  // User details & States
  const [score, setScore] = useState(0);
  const [points, setPoints] = useState(0);
  const [muted, setMuted] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [spinResult, setSpinResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessageState] = useState("");
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [nextSpinSeconds, setNextSpinSeconds] = useState(0);

  const setStatusMessage = useCallback((msg) => {
    setStatusMessageState(msg);
    if (msg) {
      setShowStatusModal(true);
    } else {
      setShowStatusModal(false);
    }
  }, []);

  // Wheel animation refs
  const isSpinningRef = useRef(false);
  const angleRef = useRef(0);
  const velocityRef = useRef(0);
  const lastPegRef = useRef(0);
  const coinsListRef = useRef([]);
  const apiResultRef = useRef(null); // holds playSpin response during animation

  const msisdn = Cookies.get("ani") || "";

  // Load real balance & eligibility from backend on mount
  useEffect(() => {
    soundCtrl.muted = false;
    if (!msisdn) return;
    checkEligibility(msisdn)
      .then((data) => {
        setPoints(data.currentPoints);
        Cookies.set("point", String(data.currentPoints));
        if (!data.canPlay) {
          setNextSpinSeconds(data.nextFreeSpinSeconds);
          setStatusMessageState(data.message);
        } else {
          setStatusMessageState("");
          setShowStatusModal(false);
        }
      })
      .catch(() => {
        // Fall back to cookies if backend unreachable
        const rawPoint = Cookies.get("point");
        setPoints(rawPoint ? parseInt(rawPoint, 10) : 0);
      });
    const rawScore = Cookies.get("Score");
    setScore(rawScore ? parseInt(rawScore, 10) : 0);
  }, [msisdn]);

  // Audio Toggle
  const toggleMute = () => {
    soundCtrl.muted = !muted;
    setMuted(!muted);
  };

  // Trigger win coin explosion
  const startCoinExplosion = () => {
    const rainCanvas = rainCanvasRef.current;
    if (!rainCanvas) return;
    const count = 75;
    for (let i = 0; i < count; i++) {
      coinsListRef.current.push(new Coin(rainCanvas.width, rainCanvas.height, true));
    }
  };

  // Main game animation loop
  useEffect(() => {
    const rainCanvas = rainCanvasRef.current;
    if (!rainCanvas) return;

    const rainCtx = rainCanvas.getContext("2d");

    // Handle Window Resizing for background coin rain
    const handleResize = () => {
      rainCanvas.width = window.innerWidth;
      rainCanvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);
    handleResize();

    // Populate ambient falling coins
    const ambientCount = 30;
    coinsListRef.current = [];
    for (let i = 0; i < ambientCount; i++) {
      coinsListRef.current.push(new Coin(rainCanvas.width, rainCanvas.height));
    }

    let animationId;
    let frame = 0;
    let pointerWiggle = 0;

    const gameLoop = () => {
      frame++;

      // 1. Render Coin Rain Canvas
      rainCtx.clearRect(0, 0, rainCanvas.width, rainCanvas.height);
      const coins = coinsListRef.current;
      for (let i = coins.length - 1; i >= 0; i--) {
        const coin = coins[i];
        coin.update(rainCanvas.width, rainCanvas.height);
        coin.draw(rainCtx);
        if (coin.isExplosion && (coin.opacity <= 0 || coin.y > rainCanvas.height)) {
          coins.splice(i, 1);
        }
      }

      // Top up ambient coins
      const currentAmbient = coins.filter((c) => !c.isExplosion).length;
      if (currentAmbient < ambientCount) {
        coins.push(new Coin(rainCanvas.width, rainCanvas.height));
      }

      // 2. Physics of Spinning Wheel
      if (isSpinningRef.current) {
        angleRef.current += velocityRef.current;
        velocityRef.current *= 0.985; // deceleration friction

        // Physically rotate wheel container using DOM ref for GPU acceleration
        if (wheelContainerRef.current) {
          wheelContainerRef.current.style.transform = `rotate(${angleRef.current}deg)`;
        }

        // Check peg ticks (peg every 45 degrees, offset by 22.5)
        const currentPeg = Math.floor((angleRef.current + 22.5) / 45);
        if (currentPeg !== lastPegRef.current) {
          soundCtrl.playTick();
          pointerWiggle = 22; // trigger indicator deflection
          lastPegRef.current = currentPeg;
        }

        // Spin complete trigger
        if (velocityRef.current < 0.05) {
          isSpinningRef.current = false;
          velocityRef.current = 0;
          
          // Determine winning segment
          const finalAngle = angleRef.current;
          const winningIndex = Math.floor(((360 - (finalAngle % 360) + 22.5) % 360) / 45);
          
          // Execute callback outside loop
          setTimeout(() => {
            handleSpinResult(winningIndex);
          }, 400);
        }
      }

      // Decay pointer wiggle back to 0
      pointerWiggle *= 0.82;
      if (pointerRef.current) {
        pointerRef.current.style.transform = `translateX(-50%) rotate(${pointerWiggle}deg)`;
      }

      animationId = requestAnimationFrame(gameLoop);
    };

    gameLoop();

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  // Start wheel animation toward the given wedge index
  // const startWheelAnimation = (targetWedge) => {
  //   const normalizedCurrentAngle = angleRef.current % 360;
  //   angleRef.current = normalizedCurrentAngle;
  //   if (wheelContainerRef.current) {
  //     wheelContainerRef.current.style.transform = `rotate(${normalizedCurrentAngle}deg)`;
  //   }
  //   const extraRotations = 5 + Math.floor(Math.random() * 3);
  //   const targetFinalAngle = (extraRotations * 360) + (360 - (targetWedge * 45));
  //   const distanceToCover = targetFinalAngle - normalizedCurrentAngle;
  //   const initialVelocity = distanceToCover * (1 - 0.985);
  //   soundCtrl.init();
  //   isSpinningRef.current = true;
  //   velocityRef.current = initialVelocity;
  //   lastPegRef.current = Math.floor((normalizedCurrentAngle + 22.5) / 45);
  // };

  const startWheelAnimation = (targetWedge) => {
  console.log("🎯 [SPIN START] targetWedge =", targetWedge);

  const normalizedCurrentAngle = angleRef.current % 360;

  console.log("📍 current raw angle:", angleRef.current);
  console.log("📍 normalized angle:", normalizedCurrentAngle);

  angleRef.current = normalizedCurrentAngle;

  if (wheelContainerRef.current) {
    wheelContainerRef.current.style.transform =
      `rotate(${normalizedCurrentAngle}deg)`;
  }

  const extraRotations = 5 + Math.floor(Math.random() * 3);

  console.log("🔄 extra rotations:", extraRotations);

  const targetFinalAngle =
    (extraRotations * 360) + (360 - (targetWedge * 45));

  console.log("🎯 target final angle:", targetFinalAngle);

  const distanceToCover =
    targetFinalAngle - normalizedCurrentAngle;

  console.log("📏 distance to cover:", distanceToCover);

  const initialVelocity =
    distanceToCover * (1 - 0.985);

  console.log("⚡ initial velocity:", initialVelocity);

  soundCtrl.init();

  isSpinningRef.current = true;
  velocityRef.current = initialVelocity;

  const currentPeg = Math.floor(
    (normalizedCurrentAngle + 22.5) / 45
  );

  console.log("📌 current peg index:", currentPeg);

  lastPegRef.current = currentPeg;

  console.log("🚀 SPIN TRIGGERED");
};

  // Execute Spin trigger — calls backend, enforces daily limit & balance server-side
  const spinWheel = async () => {
    if (isSpinningRef.current || isLoading) return;

    if (!msisdn) {
      setStatusMessage("User not identified. Please log in again.");
      return;
    }

    setIsLoading(true);
    setStatusMessage("");

    try {
      // Check eligibility first dynamically on click
      const elig = await checkEligibility(msisdn);
      setPoints(elig.currentPoints);
      setNextSpinSeconds(elig.nextFreeSpinSeconds);

      if (!elig.canPlay) {
        setStatusMessage(elig.message);
        setIsLoading(false);
        return;
      }

      // Backend enforces: 1 spin/day + minimum 50 points balance
      const data = await playSpin(msisdn);

      // Store result so handleSpinResult can use it after animation
      apiResultRef.current = data;

      // Map backend rewardType → correct front-end wedge index
      let targetWedge = REWARD_TYPE_TO_WEDGE_INDEX[data.rewardType];
      console.log(targetWedge,"==targetwedge==")
      if (targetWedge === undefined || targetWedge === null) {
        // LOSE: randomly pick segment 4 or 7
        targetWedge = Math.random() < 0.5 ? 4 : 7;
      }

      // Update displayed balance optimistically (actual comes from backend)
      setPoints(data.totalPoints);
      Cookies.set("point", String(data.totalPoints));

      // Kick off the visual spin animation
      startWheelAnimation(targetWedge);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Spin failed. Please try again.";
      setStatusMessage(String(msg));
      soundCtrl.playLose();
    } finally {
      setIsLoading(false);
    }
  };

  // Process win outcome — uses backend result, not local wedge data
  const handleSpinResult = useCallback((index) => {
    const apiData = apiResultRef.current;

    // Determine visual result from wedge array (for display label/icon)
    const visualResult = wedges[index];

    // Build result object: prefer backend data for points, use visual for label/icon
    const result = {
      ...visualResult,
      // Override with real backend values
      isWin: apiData ? apiData.rewardPoints > 0 : visualResult.isWin,
      value: apiData ? apiData.rewardPoints : visualResult.value,
      backendMessage: apiData ? apiData.message : "",
    };

    setSpinResult(result);
    setShowResultModal(true);

    const totalPoints = apiData ? apiData.totalPoints : points;
    const dateStr =
      new Date().toLocaleDateString("en-GB") +
      " " +
      new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

    // Update local history for Dashboard display
    let spinHistory = JSON.parse(localStorage.getItem("spinHistory") || "[]");
    spinHistory.unshift({
      date: dateStr,
      reward: result.label + (result.subLabel ? " " + result.subLabel : ""),
      points: result.isWin ? "+" + result.value.toLocaleString() : "0",
    });
    spinHistory = spinHistory.slice(0, 10);
    localStorage.setItem("spinHistory", JSON.stringify(spinHistory));

    let totalSpins = parseInt(localStorage.getItem("totalSpins") || "0", 10);
    localStorage.setItem("totalSpins", totalSpins + 1);

    if (result.isWin) {
      soundCtrl.playWin();
      startCoinExplosion();
      let totalWins = parseInt(localStorage.getItem("totalWins") || "0", 10);
      localStorage.setItem("totalWins", totalWins + 1);
      const newScore = score + result.value;
      setScore(newScore);
      Cookies.set("Score", String(newScore));
    } else {
      soundCtrl.playLose();
    }

    setPoints(totalPoints);
    Cookies.set("point", String(totalPoints));

    // After spin, fetch dynamic eligibility message in background
    checkEligibility(msisdn).then((d) => {
      setPoints(d.currentPoints);
      setNextSpinSeconds(d.nextFreeSpinSeconds);
      setStatusMessageState(d.message);
    }).catch(() => {
      setNextSpinSeconds(86400);
      setStatusMessageState("You have already spun today. Come back tomorrow!");
    });
    apiResultRef.current = null;
  }, [score, points, msisdn]);

  return (
    <div className="relative min-h-screen w-full flex flex-col items-center justify-start text-white overflow-hidden select-none bg-gradient-to-b from-[#251F0E] via-[#0E0D07] to-[#050502]">
      
      {/* Dynamic Rain Canvas */}
      <canvas ref={rainCanvasRef} className="absolute inset-0 pointer-events-none z-0 w-full h-full" />

      {/* Header bar UI */}
      <header className="relative w-full max-w-6xl mx-auto px-4 pt-4 pb-2 flex items-center justify-between z-10">
        {/* <h1 className="text-xl md:text-3xl font-black tracking-widest text-[#D4AF37] drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
          LUCKY SPIN
        </h1> */}

        <div className="flex items-center gap-3">
          {/* Mute Button */}
          <button 
            onClick={toggleMute}
            className="w-10 h-10 rounded-full border border-yellow-500/50 bg-black/40 flex items-center justify-center cursor-pointer hover:border-yellow-400 hover:scale-105 active:scale-95 transition-all text-yellow-500 shadow-md"
            title={muted ? "Unmute Audio" : "Mute Audio"}
          >
            {muted ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 9.75L19.5 12m0 0l2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25m-10.5-6L4.5 9H1.5v6h3l4.5 3.75V5.25z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
              </svg>
            )}
          </button>

          {/* Points Pill Container */}
          <div className="px-4 py-1.5 rounded-full border-2 border-yellow-500 bg-black/60 shadow-lg shadow-yellow-500/10 flex items-center justify-center min-w-[110px]">
            <span className="text-sm font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500">
              {points.toLocaleString()} PTS
            </span>
          </div>

         
        </div>
      </header>

      {/* Main Wheel Area */}
      <main className="relative flex-1 flex flex-col items-center justify-center z-10 px-4 py-0 w-full">
        
        {/* Total Score Banner */}
        <div className="mb-4 bg-yellow-500/10 border border-yellow-500/30 px-6 py-1.5 rounded-md backdrop-blur-sm">
          <span className="text-yellow-400 font-bold text-sm uppercase tracking-widest">
            Score: <span className="text-white font-black text-base ml-1">{score.toLocaleString()}</span>
          </span>
        </div>

        {/* Outer Cabinet Wrapper */}
        {/* <div className="relative w-[95vw] h-[95vw] max-w-[540px] max-h-[540px] aspect-square flex items-center justify-center"> */}
          <div className="relative w-[95vw] h-[95vw] max-w-[520px] max-h-[500px] aspect-square flex items-center justify-center">
          
          {/* Background rays constrained to a circle behind the wheel */}
          <div 
            className="absolute inset-[-40%] pointer-events-none opacity-30 animate-[spin_60s_linear_infinite]"
            style={{
              backgroundImage: "repeating-conic-gradient(from 0deg, #D4AF37 0deg, #D4AF37 15deg, transparent 15deg, transparent 30deg)",
              maskImage: "radial-gradient(circle, black 30%, transparent 65%)",
              WebkitMaskImage: "radial-gradient(circle, black 30%, transparent 65%)"
            }}
          />

          {/* Red Crystal Indicator / Pointer at 12 o'clock */}
          <div 
            ref={pointerRef}
            className="absolute top-[-9.5%] left-1/2 -translate-x-1/2 z-30 transition-transform origin-top duration-75 pointer-events-none"
          >
            {/* Image Arrow Pointer */}
            <img src={arrowImg} alt="Pointer" className="w-[26vw] max-w-[140px] drop-shadow-2xl" />
          </div>

          {/* Clickable Wrapper with tactile feedback */}
          <div 
            onClick={spinWheel}
            className={`relative w-full h-full rounded-full transition-transform duration-150 ease-out ${!(isSpinningRef.current || isLoading) ? 'cursor-pointer hover:scale-[1.02] active:scale-[0.98]' : ''}`}
          >
            {/* Inner Rotating Spin Wheel Canvas container */}
            <div 
              ref={wheelContainerRef}
              className="w-full h-full rounded-full overflow-hidden z-0"
            >
              <img 
                src={wheelImg} 
                alt="Spin Wheel" 
                className="w-full h-full object-contain block drop-shadow-lg" 
              />
            </div>
          </div>
        </div>

        {/* Back Button */}
        <button
          onClick={() => {
            window.location.href = "/home";
          }}
          className="mt-3 px-8 py-2 rounded-lg border border-yellow-500/40 bg-gradient-to-b from-yellow-500/20 to-yellow-600/10 hover:from-yellow-500/30 hover:to-yellow-600/20 text-yellow-400 font-bold text-sm tracking-wider uppercase transition-all shadow-md active:scale-95 cursor-pointer z-20"
        >
          Back
        </button>

        {/* Status / Daily-limit message */}
        {/* {statusMessage ? (
          <p className="mt-4 text-xs font-semibold text-center px-4 text-red-400 tracking-wide">
            {statusMessage}
          </p>
        ) : null} */}

        {/* Cost / Helper description */}
        {/* <p className="mt-6 text-xs text-yellow-500/60 font-medium tracking-wide">
          Costs 100 PTS per spin
        </p> */}

      </main>

      {/* Footer copyright section */}
      {/* <footer className="relative w-full text-center py-4 z-10">
        <p className="text-[10px] text-gray-600 font-bold uppercase tracking-widest">
          &copy; {new Date().getFullYear()} Wheel of Fortune. All rights reserved.
        </p>
      </footer> */}

      {/* Reward / Better Luck Next Time popup Modal */}
      {showResultModal && spinResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-[fadeIn_0.2s_ease-out]">
          <div className="relative w-full max-w-sm border-2 border-yellow-500/60 bg-gradient-to-b from-[#211E15] to-[#0A0905] rounded-2xl p-6 text-center shadow-2xl shadow-yellow-500/10">
            
            {/* Close Icon on top-right */}
            <button
              onClick={() => setShowResultModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors cursor-pointer focus:outline-none z-20"
              aria-label="Close modal"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Glowing background rays on win */}
            {spinResult.isWin && (
              <div className="absolute inset-0 pointer-events-none opacity-20 bg-[radial-gradient(circle_at_center,_#D4AF37_0%,_transparent_60%)] animate-pulse" />
            )}

            {/* Modal Content */}
            <div className="relative z-10">
              {/* Icon / Emoji header */}
              <div className="text-6xl mb-3 animate-bounce">
                {spinResult.icon}
              </div>

              <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-amber-500 tracking-wider uppercase mb-1">
                {spinResult.isWin ? "Congratulations!" : "Too Bad!"}
              </h3>
              
              <p className="text-xs text-gray-400 font-semibold tracking-wider uppercase mb-4">
                {spinResult.isWin ? "You landed on" : "No luck this time"}
              </p>

              <div className="bg-black/60 border border-yellow-500/20 py-4 px-3 rounded-lg mb-6">
                <span className="block text-3xl font-black text-yellow-400 tracking-wide">
                  {spinResult.label}
                </span>
                {spinResult.subLabel && (
                  <span className="block text-xs font-bold text-gray-300 uppercase tracking-widest mt-1">
                    {spinResult.subLabel}
                  </span>
                )}
              </div>

              <p className="text-sm text-yellow-500/80 font-medium mb-6">
                {spinResult.backendMessage || (spinResult.isWin ? `${spinResult.value.toLocaleString()} points added to your balance!` : "Don't lose hope. Try your luck again!")}
              </p>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setShowResultModal(false)}
                  className="px-6 py-2 rounded-lg border border-yellow-500/40 bg-gradient-to-b from-yellow-500/20 to-yellow-600/10 hover:from-yellow-500/30 hover:to-yellow-600/20 text-yellow-400 font-bold text-sm tracking-wider uppercase transition-all shadow-md active:scale-95 cursor-pointer"
                >
                  Spin Again
                </button>
                <Link
                  to="/home"
                  className="px-6 py-2 rounded-lg border border-gray-600/40 bg-gray-900/60 hover:bg-gray-800 text-gray-300 font-bold text-sm tracking-wider uppercase transition-all shadow-md active:scale-95"
                >
                  Go Home
                </Link>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Error / Status popup Modal */}
      {showStatusModal && statusMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-[fadeIn_0.2s_ease-out]">
          <div className="relative w-full max-w-sm border-2 border-red-500/60 bg-gradient-to-b from-[#211E15] to-[#0A0905] rounded-2xl p-6 text-center shadow-2xl shadow-red-500/10">
            
            {/* Close Button on Right Side */}
            <button
              onClick={() => {
                setShowStatusModal(false);
                setStatusMessageState("");
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors cursor-pointer focus:outline-none z-20"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="h-6 w-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Modal Content */}
            <div className="relative z-10 py-4">
              {/* Info / Alert Icon */}
              <div className="text-5xl mb-4 text-red-500">
                ⚠️
              </div>

              <h3 className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-amber-500 tracking-wider uppercase mb-3">
                Notification
              </h3>

              <p className="text-sm text-gray-200 font-medium mb-6">
                {statusMessage}
              </p>

              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setStatusMessageState("");
                }}
                className="px-6 py-2 rounded-lg border border-red-500/40 bg-gradient-to-b from-red-500/20 to-red-600/10 hover:from-red-500/30 hover:to-red-600/20 text-red-400 font-bold text-sm tracking-wider uppercase transition-all shadow-md active:scale-95 cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameScene;




// import React, { useEffect, useRef, useState } from "react";
// import Cookies from "js-cookie";
// import { Link, useNavigate } from "react-router-dom";
// import wheelImg from "../assets/wheel.png";
// import arrowImg from "../assets/arrow.png";

// // Web Audio API Sound Controller for arcade-quality audio effects
// class SoundController {
//   constructor() {
//     this.ctx = null;
//     this.muted = false;
//   }
//   init() {
//     if (!this.ctx) {
//       this.ctx = new (window.AudioContext || window.webkitAudioContext)();
//     }
//     if (this.ctx.state === "suspended") {
//       this.ctx.resume();
//     }
//   }
//   playTick() {
//     if (this.muted) return;
//     this.init();
//     try {
//       const osc = this.ctx.createOscillator();
//       const gain = this.ctx.createGain();
//       osc.connect(gain);
//       gain.connect(this.ctx.destination);
//       osc.type = "triangle";
//       osc.frequency.setValueAtTime(600, this.ctx.currentTime);
//       osc.frequency.exponentialRampToValueAtTime(120, this.ctx.currentTime + 0.04);
//       gain.gain.setValueAtTime(0.12, this.ctx.currentTime);
//       gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.04);
//       osc.start();
//       osc.stop(this.ctx.currentTime + 0.04);
//     } catch (e) {
//       console.warn("Audio play blocked or unsupported:", e);
//     }
//   }
//   playWin() {
//     if (this.muted) return;
//     this.init();
//     try {
//       const now = this.ctx.currentTime;
//       const playTone = (freq, startTime, duration) => {
//         const osc = this.ctx.createOscillator();
//         const gain = this.ctx.createGain();
//         osc.connect(gain);
//         gain.connect(this.ctx.destination);
//         osc.type = "sine";
//         osc.frequency.setValueAtTime(freq, startTime);
//         gain.gain.setValueAtTime(0, startTime);
//         gain.gain.linearRampToValueAtTime(0.2, startTime + 0.04);
//         gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
//         osc.start(startTime);
//         osc.stop(startTime + duration);
//       };
//       playTone(523.25, now, 0.12);
//       playTone(659.25, now + 0.1, 0.12);
//       playTone(783.99, now + 0.2, 0.12);
//       playTone(1046.50, now + 0.3, 0.5);
//     } catch (e) {
//       console.warn("Audio play blocked or unsupported:", e);
//     }
//   }
//   playLose() {
//     if (this.muted) return;
//     this.init();
//     try {
//       const now = this.ctx.currentTime;
//       const playTone = (freq, startTime, duration) => {
//         const osc = this.ctx.createOscillator();
//         const gain = this.ctx.createGain();
//         osc.connect(gain);
//         gain.connect(this.ctx.destination);
//         osc.type = "sawtooth";
//         osc.frequency.setValueAtTime(freq, startTime);
//         osc.frequency.linearRampToValueAtTime(freq - 80, startTime + duration);
//         gain.gain.setValueAtTime(0.15, startTime);
//         gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
//         osc.start(startTime);
//         osc.stop(startTime + duration);
//       };
//       playTone(330, now, 0.15);
//       playTone(220, now + 0.18, 0.35);
//     } catch (e) {
//       console.warn("Audio play blocked or unsupported:", e);
//     }
//   }
// }

// const soundCtrl = new SoundController();

// // Wheel wedges config matching user request and screenshot
// const wedges = [
//   { label: "5,000", subLabel: "COINS", color: "#A30000", textColor: "#FFFFFF", icon: "🪙", value: 5000, isWin: true },
//   { label: "10,000", subLabel: "COINS", color: "#0A1931", textColor: "#FFFFFF", icon: "🪙", value: 10000, isWin: true },
//   { label: "30,000", subLabel: "COINS", color: "#064E3B", textColor: "#FFFFFF", icon: "💼", value: 30000, isWin: true },
//   { label: "JACKPOT", subLabel: "", color: "#92400E", textColor: "#FFFFFF", icon: "👑", value: 100000, isWin: true },
//   { label: "BETTER LUCK", subLabel: "NEXT TIME", color: "#1E1B4B", textColor: "#FFFFFF", icon: "😢", value: 0, isWin: false },
//   { label: "50,000", subLabel: "COINS", color: "#0F766E", textColor: "#FFFFFF", icon: "💰", value: 50000, isWin: true },
//   { label: "20,000", subLabel: "COINS", color: "#4C1D95", textColor: "#FFFFFF", icon: "💎", value: 20000, isWin: true },
//   { label: "BETTER LUCK", subLabel: "NEXT TIME", color: "#1E1B4B", textColor: "#FFFFFF", icon: "😢", value: 0, isWin: false }
// ];

// // 3D-like falling gold coin class
// class Coin {
//   constructor(canvasWidth, canvasHeight, isExplosion = false) {
//     this.canvasWidth = canvasWidth;
//     this.canvasHeight = canvasHeight;
//     this.isExplosion = isExplosion;
    
//     this.x = isExplosion ? canvasWidth / 2 + (Math.random() - 0.5) * 80 : Math.random() * canvasWidth;
//     this.y = isExplosion ? canvasHeight / 2 + (Math.random() - 0.5) * 80 : -50 - Math.random() * 300;
//     this.radius = isExplosion ? Math.random() * 7 + 5 : Math.random() * 11 + 6;
    
//     // Physics velocities
//     if (isExplosion) {
//       const angle = Math.random() * Math.PI * 2;
//       const speed = Math.random() * 12 + 4;
//       this.vx = Math.cos(angle) * speed;
//       this.vy = Math.sin(angle) * speed - Math.random() * 5;
//       this.gravity = 0.3;
//     } else {
//       this.vx = (Math.random() - 0.5) * 1.2;
//       this.vy = Math.random() * 2.2 + 1.6;
//       this.gravity = 0;
//     }
    
//     this.rotation = Math.random() * Math.PI * 2;
//     this.rotationSpeed = (Math.random() - 0.5) * 0.08;
//     this.flipAngle = Math.random() * Math.PI;
//     this.flipSpeed = Math.random() * 0.06 + 0.04;
//     this.opacity = Math.random() * 0.3 + 0.7;
//     this.scale = isExplosion ? Math.random() * 0.4 + 0.6 : Math.random() * 0.6 + 0.4;
//   }

//   update(canvasWidth, canvasHeight) {
//     if (this.isExplosion) {
//       this.vy += this.gravity;
//       this.opacity -= 0.008;
//     }
    
//     this.x += this.vx;
//     this.y += this.vy;
//     this.rotation += this.rotationSpeed;
//     this.flipAngle += this.flipSpeed;
    
//     // Recycle standard rain coins
//     if (!this.isExplosion && this.y > canvasHeight + 30) {
//       this.y = -30;
//       this.x = Math.random() * canvasWidth;
//       this.vy = Math.random() * 2.2 + 1.6;
//       this.vx = (Math.random() - 0.5) * 1.2;
//     }
//   }

//   draw(ctx) {
//     if (this.opacity <= 0) return;
    
//     ctx.save();
//     ctx.translate(this.x, this.y);
//     ctx.scale(Math.cos(this.flipAngle) * this.scale, 1 * this.scale);
//     ctx.rotate(this.rotation);
//     ctx.globalAlpha = this.opacity;
    
//     // Depth of field blur for distant items
//     if (this.scale < 0.55 && !this.isExplosion) {
//       ctx.filter = "blur(1px)";
//     }

//     // Gold coin base gradient
//     ctx.beginPath();
//     ctx.arc(0, 0, this.radius, 0, Math.PI * 2);
//     const grad = ctx.createRadialGradient(-this.radius/3, -this.radius/3, this.radius/10, 0, 0, this.radius);
//     grad.addColorStop(0, "#FFE875"); // shiny yellow
//     grad.addColorStop(0.5, "#F39C12"); // warm gold
//     grad.addColorStop(1, "#B7950B"); // dark bronze outline
//     ctx.fillStyle = grad;
//     ctx.fill();

//     // Embossed rim border
//     ctx.lineWidth = this.radius * 0.12;
//     ctx.strokeStyle = "#9A7D0A";
//     ctx.stroke();

//     // Inner ring detail
//     ctx.beginPath();
//     ctx.arc(0, 0, this.radius * 0.65, 0, Math.PI * 2);
//     ctx.lineWidth = this.radius * 0.05;
//     ctx.strokeStyle = "#F5B041";
//     ctx.stroke();

//     // Center emblem (star/symbol)
//     ctx.font = `bold ${this.radius * 0.75}px sans-serif`;
//     ctx.fillStyle = "#7D6608";
//     ctx.textAlign = "center";
//     ctx.textBaseline = "middle";
//     ctx.fillText("$", 0, 0);

//     ctx.restore();
//     ctx.filter = "none";
//   }
// }

// const GameScene = () => {
//   const navigate = useNavigate();
//   const rainCanvasRef = useRef(null);
//   const bulbsCanvasRef = useRef(null);
//   const pointerRef = useRef(null);
//   const wheelContainerRef = useRef(null);

//   // User details & States
//   const [score, setScore] = useState(0);
//   const [points, setPoints] = useState(0);
//   const [muted, setMuted] = useState(false);
//   const [showResultModal, setShowResultModal] = useState(false);
//   const [spinResult, setSpinResult] = useState(null);

//   // Wheel animation refs
//   const isSpinningRef = useRef(false);
//   const angleRef = useRef(0);
//   const velocityRef = useRef(0);
//   const lastPegRef = useRef(0);
//   const coinsListRef = useRef([]);

//   // Load points and scores on mount
//   useEffect(() => {
//     const rawScore = Cookies.get("Score");
//     const rawPoint = Cookies.get("point");
    
//     // Parse cookies or setup persist defaults
//     const scoreVal = rawScore ? parseInt(rawScore, 10) : 0;
//     const pointsVal = rawPoint ? parseInt(rawPoint, 10) : 5000;
    
//     setScore(scoreVal);
//     setPoints(pointsVal);

//     // Sync to cookies in case of new user
//     if (!rawScore) Cookies.set("Score", "0");
//     if (!rawPoint) Cookies.set("point", "5000");

//     // Initialize Web Audio toggle
//     soundCtrl.muted = false;
//   }, []);

//   // Audio Toggle
//   const toggleMute = () => {
//     soundCtrl.muted = !muted;
//     setMuted(!muted);
//   };

//   // Trigger win coin explosion
//   const startCoinExplosion = () => {
//     const rainCanvas = rainCanvasRef.current;
//     if (!rainCanvas) return;
//     const count = 75;
//     for (let i = 0; i < count; i++) {
//       coinsListRef.current.push(new Coin(rainCanvas.width, rainCanvas.height, true));
//     }
//   };

//   // Main game animation loop
//   useEffect(() => {
//     const rainCanvas = rainCanvasRef.current;
//     const bulbsCanvas = bulbsCanvasRef.current;
//     if (!rainCanvas || !bulbsCanvas) return;

//     const rainCtx = rainCanvas.getContext("2d");
//     const bulbsCtx = bulbsCanvas.getContext("2d");

//     // Bulbs size matching container
//     const size = 530;
//     bulbsCanvas.width = size;
//     bulbsCanvas.height = size;

//     // Handle Window Resizing for background coin rain
//     const handleResize = () => {
//       rainCanvas.width = window.innerWidth;
//       rainCanvas.height = window.innerHeight;
//     };
//     window.addEventListener("resize", handleResize);
//     handleResize();

//     // Populate ambient falling coins
//     const ambientCount = 30;
//     coinsListRef.current = [];
//     for (let i = 0; i < ambientCount; i++) {
//       coinsListRef.current.push(new Coin(rainCanvas.width, rainCanvas.height));
//     }

//     let animationId;
//     let frame = 0;
//     let pointerWiggle = 0;

//     const gameLoop = () => {
//       frame++;

//       // 1. Render Coin Rain Canvas
//       rainCtx.clearRect(0, 0, rainCanvas.width, rainCanvas.height);
//       const coins = coinsListRef.current;
//       for (let i = coins.length - 1; i >= 0; i--) {
//         const coin = coins[i];
//         coin.update(rainCanvas.width, rainCanvas.height);
//         coin.draw(rainCtx);
//         if (coin.isExplosion && (coin.opacity <= 0 || coin.y > rainCanvas.height)) {
//           coins.splice(i, 1);
//         }
//       }

//       // Top up ambient coins
//       const currentAmbient = coins.filter((c) => !c.isExplosion).length;
//       if (currentAmbient < ambientCount) {
//         coins.push(new Coin(rainCanvas.width, rainCanvas.height));
//       }

//       // 2. Physics of Spinning Wheel
//       if (isSpinningRef.current) {
//         angleRef.current += velocityRef.current;
//         velocityRef.current *= 0.985; // deceleration friction

//         // Physically rotate wheel container using DOM ref for GPU acceleration
//         if (wheelContainerRef.current) {
//           wheelContainerRef.current.style.transform = `rotate(${angleRef.current}deg)`;
//         }

//         // Check peg ticks (peg every 45 degrees, offset by 22.5)
//         const currentPeg = Math.floor((angleRef.current + 22.5) / 45);
//         if (currentPeg !== lastPegRef.current) {
//           soundCtrl.playTick();
//           pointerWiggle = 22; // trigger indicator deflection
//           lastPegRef.current = currentPeg;
//         }

//         // Spin complete trigger
//         if (velocityRef.current < 0.05) {
//           isSpinningRef.current = false;
//           velocityRef.current = 0;
          
//           // Determine winning segment
//           const finalAngle = angleRef.current;
//           const winningIndex = Math.floor(((360 - (finalAngle % 360) + 22.5) % 360) / 45);
          
//           // Execute callback outside loop
//           setTimeout(() => {
//             handleSpinResult(winningIndex);
//           }, 400);
//         }
//       }

//       // Decay pointer wiggle back to 0
//       pointerWiggle *= 0.82;
//       if (pointerRef.current) {
//         pointerRef.current.style.transform = `translateX(-50%) rotate(${pointerWiggle}deg)`;
//       }

//       // 3. Draw outer golden cabinet border & blinking lights
//       bulbsCtx.clearRect(0, 0, size, size);
//       const center = size / 2;
//       const rimRadius = size / 2 - 20;

//       // Draw thick outer golden border ring
//       bulbsCtx.save();
//       bulbsCtx.beginPath();
//       bulbsCtx.arc(center, center, rimRadius + 6, 0, Math.PI * 2);
//       const outerRingGrad = bulbsCtx.createRadialGradient(center - 10, center - 10, rimRadius - 10, center, center, rimRadius + 15);
//       outerRingGrad.addColorStop(0, "#F39C12");
//       outerRingGrad.addColorStop(0.3, "#FFE875");
//       outerRingGrad.addColorStop(0.7, "#D4AF37");
//       outerRingGrad.addColorStop(1, "#5E4601");
//       bulbsCtx.lineWidth = 14;
//       bulbsCtx.strokeStyle = outerRingGrad;
//       bulbsCtx.stroke();

//       // Shadow border
//       bulbsCtx.beginPath();
//       bulbsCtx.arc(center, center, rimRadius, 0, Math.PI * 2);
//       bulbsCtx.lineWidth = 2;
//       bulbsCtx.strokeStyle = "#7D6608";
//       bulbsCtx.stroke();
//       bulbsCtx.restore();

//       // Draw light bulbs on rim
//       const bulbCount = 24;
//       const blinkingPhase = Math.floor(frame / 12) % 2; // alternates speed

//       for (let i = 0; i < bulbCount; i++) {
//         const theta = (i * (360 / bulbCount) * Math.PI) / 180;
//         const bulbX = center + Math.cos(theta) * rimRadius;
//         const bulbY = center + Math.sin(theta) * rimRadius;

//         bulbsCtx.save();
//         bulbsCtx.beginPath();
//         bulbsCtx.arc(bulbX, bulbY, 5.5, 0, Math.PI * 2);

//         // Bulbs blinking alternate phases
//         const isOn = (i + blinkingPhase) % 2 === 0;

//         if (isOn) {
//           // Glow bulb
//           bulbsCtx.shadowColor = "#FFEB3B";
//           bulbsCtx.shadowBlur = 12;
//           const bulbGrad = bulbsCtx.createRadialGradient(bulbX - 2, bulbY - 2, 1, bulbX, bulbY, 5.5);
//           bulbGrad.addColorStop(0, "#FFFFFF");
//           bulbGrad.addColorStop(0.4, "#FFF59D");
//           bulbGrad.addColorStop(1, "#FBC02D");
//           bulbsCtx.fillStyle = bulbGrad;
//         } else {
//           // Dim bulb
//           const bulbGrad = bulbsCtx.createRadialGradient(bulbX - 1, bulbY - 1, 1, bulbX, bulbY, 5.5);
//           bulbGrad.addColorStop(0, "#FBC02D");
//           bulbGrad.addColorStop(1, "#7D6608");
//           bulbsCtx.fillStyle = bulbGrad;
//         }
//         bulbsCtx.fill();
//         bulbsCtx.restore();
//       }

//       animationId = requestAnimationFrame(gameLoop);
//     };

//     gameLoop();

//     return () => {
//       window.removeEventListener("resize", handleResize);
//       cancelAnimationFrame(animationId);
//     };
//   }, []);

//   // Execute Spin trigger
//   const spinWheel = () => {
//     if (isSpinningRef.current) return;
    
//     // cost of playing: 100 points
//     if (points < 100) {
//       soundCtrl.playLose();
//       alert("Insufficient Points to Spin! You need at least 100 PTS.");
//       return;
//     }

//     // Deduct cost
//     const newPoints = points - 100;
//     setPoints(newPoints);
//     Cookies.set("point", String(newPoints));

//     // Reset rotation angle modulo to keep within standard bounds
//     const normalizedCurrentAngle = angleRef.current % 360;
//     angleRef.current = normalizedCurrentAngle;
//     if (wheelContainerRef.current) {
//       wheelContainerRef.current.style.transform = `rotate(${normalizedCurrentAngle}deg)`;
//     }

//     // Set starting speed (adds 5 to 7 full rotations plus random offset)
//     const extraRotations = 5 + Math.floor(Math.random() * 3);
//     const targetWedge = Math.floor(Math.random() * wedges.length);
    
//     // Math to solve exactly where the wheel must rotate to land on targetWedge at 12 o'clock pointer
//     // Formula: Target final angle = extraRotations * 360 + (360 - (targetWedge * 45))
//     // Removed the 22.5 degree offset so it lands perfectly in the center of the image wedge
//     const targetFinalAngle = (extraRotations * 360) + (360 - (targetWedge * 45));
    
//     // We want the friction formula `velocity *= 0.985` over N steps to sum up exactly to targetFinalAngle.
//     // Analytical solution for initial velocity:
//     // Distance = initial_velocity / (1 - friction)
//     // initial_velocity = Distance * (1 - 0.985)
//     const distanceToCover = targetFinalAngle - normalizedCurrentAngle;
//     const initialVelocity = distanceToCover * (1 - 0.985);
    
//     // Play sounds
//     soundCtrl.init();
//     isSpinningRef.current = true;
//     velocityRef.current = initialVelocity;
//     lastPegRef.current = Math.floor((normalizedCurrentAngle + 22.5) / 45);
//   };

//   // Process win outcome
//   const handleSpinResult = (index) => {
//     const result = wedges[index];
//     setSpinResult(result);
//     setShowResultModal(true);

//     // Track total spins
//     let totalSpins = parseInt(localStorage.getItem('totalSpins') || '0', 10);
//     localStorage.setItem('totalSpins', totalSpins + 1);

//     // Track history
//     let spinHistory = JSON.parse(localStorage.getItem('spinHistory') || '[]');
//     const dateStr = new Date().toLocaleDateString('en-GB') + ' ' + new Date().toLocaleTimeString('en-US', {hour: '2-digit', minute:'2-digit'});
//     spinHistory.unshift({
//       date: dateStr,
//       reward: result.label + (result.subLabel ? ' ' + result.subLabel : ''),
//       points: result.isWin ? '+' + result.value.toLocaleString() : '0'
//     });
//     spinHistory = spinHistory.slice(0, 10); // Keep last 10
//     localStorage.setItem('spinHistory', JSON.stringify(spinHistory));

//     if (result.isWin) {
//       soundCtrl.playWin();
//       startCoinExplosion();

//       // Track total wins
//       let totalWins = parseInt(localStorage.getItem('totalWins') || '0', 10);
//       localStorage.setItem('totalWins', totalWins + 1);

//       // Update Points and Scores
//       const newPoints = points + result.value;
//       const newScore = score + result.value;
//       setPoints(newPoints);
//       setScore(newScore);
      
//       Cookies.set("point", String(newPoints));
//       Cookies.set("Score", String(newScore));
//     } else {
//       soundCtrl.playLose();
//     }
//   };

//   return (
//     <div className="relative min-h-screen flex flex-col items-center justify-start text-white overflow-hidden select-none bg-gradient-to-b from-[#251F0E] via-[#0E0D07] to-[#050502]">
      
//       {/* Dynamic Rain Canvas */}
//       <canvas ref={rainCanvasRef} className="absolute inset-0 pointer-events-none z-0 w-full h-full" />

//       {/* Header bar UI */}
//       <header className="relative w-full max-w-6xl mx-auto px-4 pt-4 pb-2 flex items-center justify-between z-10">
//         {/* <h1 className="text-xl md:text-3xl font-black tracking-widest text-[#D4AF37] drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
//           LUCKY SPIN
//         </h1> */}

//         <div className="flex items-center gap-3">
//           {/* Mute Button */}
//           <button 
//             onClick={toggleMute}
//             className="w-10 h-10 rounded-full border border-yellow-500/50 bg-black/40 flex items-center justify-center cursor-pointer hover:border-yellow-400 hover:scale-105 active:scale-95 transition-all text-yellow-500 shadow-md"
//             title={muted ? "Unmute Audio" : "Mute Audio"}
//           >
//             {muted ? (
//               <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
//                 <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 9.75L19.5 12m0 0l2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25m-10.5-6L4.5 9H1.5v6h3l4.5 3.75V5.25z" />
//               </svg>
//             ) : (
//               <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
//                 <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
//               </svg>
//             )}
//           </button>

//           {/* Points Pill Container */}
//           <div className="px-4 py-1.5 rounded-full border-2 border-yellow-500 bg-black/60 shadow-lg shadow-yellow-500/10 flex items-center justify-center min-w-[110px]">
//             <span className="text-sm font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500">
//               {points.toLocaleString()} PTS
//             </span>
//           </div>

         
//         </div>
//       </header>

//       {/* Main Wheel Area */}
//       <main className="relative flex-1 flex flex-col items-center justify-center z-10 px-4 py-0 w-full">
        
//         {/* Total Score Banner */}
//         <div className="mb-4 bg-yellow-500/10 border border-yellow-500/30 px-6 py-1.5 rounded-md backdrop-blur-sm">
//           <span className="text-yellow-400 font-bold text-sm uppercase tracking-widest">
//             Score: <span className="text-white font-black text-base ml-1">{score.toLocaleString()}</span>
//           </span>
//         </div>

//         {/* Outer Cabinet Wrapper */}
//         <div className="relative w-[90vw] h-[90vw] max-w-[480px] max-h-[480px] aspect-square flex items-center justify-center">
          
//           {/* Background rays constrained to a circle behind the wheel */}
//           <div 
//             className="absolute inset-[-40%] pointer-events-none opacity-30 animate-[spin_60s_linear_infinite]"
//             style={{
//               backgroundImage: "repeating-conic-gradient(from 0deg, #D4AF37 0deg, #D4AF37 15deg, transparent 15deg, transparent 30deg)",
//               maskImage: "radial-gradient(circle, black 30%, transparent 65%)",
//               WebkitMaskImage: "radial-gradient(circle, black 30%, transparent 65%)"
//             }}
//           />

//           {/* Stationary outer golden cabinet ring with LED blinking bulbs */}
//           <canvas 
//             ref={bulbsCanvasRef} 
//             className="absolute inset-0 pointer-events-none z-10 w-full h-full" 
//           />

//           {/* Red Crystal Indicator / Pointer at 12 o'clock */}
//           <div 
//             ref={pointerRef}
//             className="absolute top-[-3%] left-1/2 -translate-x-1/2 z-30 transition-transform origin-top duration-75 pointer-events-none"
//           >
//             {/* Image Arrow Pointer */}
//             <img src={arrowImg} alt="Pointer" className="w-[18vw] max-w-[100px] drop-shadow-2xl" />
//           </div>

//           {/* Inner Rotating Spin Wheel Canvas container */}
//           <div 
//             ref={wheelContainerRef}
//             className="relative w-[94%] h-[94%] rounded-full overflow-hidden transition-transform duration-75 z-0"
//           >
//             <img 
//               src={wheelImg} 
//               alt="Spin Wheel" 
//               className="w-full h-full object-contain block drop-shadow-lg" 
//             />
//           </div>

//           {/* Stationary Center SPIN Button */}
//           <button 
//             onClick={spinWheel}
//             disabled={isSpinningRef.current}
//             className="absolute z-20 w-[28%] h-[28%] rounded-full border-4 border-[#F39C12] bg-gradient-to-b from-[#FFF095] via-[#D4AF37] to-[#7D6608] shadow-[0_6px_20px_rgba(0,0,0,0.6)] flex items-center justify-center cursor-pointer hover:scale-105 active:scale-95 disabled:scale-100 disabled:opacity-90 transition-transform duration-100 ease-out focus:outline-none"
//           >
//             <div className="w-[85%] h-[85%] rounded-full bg-black border-2 border-[#D4AF37] flex flex-col items-center justify-center select-none">
//               <span className="text-xl md:text-2xl font-black tracking-wider text-transparent bg-clip-text bg-gradient-to-b from-[#FFF59D] via-[#FBC02D] to-[#F39C12]">
//                 SPIN
//               </span>
//             </div>
//           </button>
//         </div>

//         {/* Cost / Helper description */}
//         {/* <p className="mt-6 text-xs text-yellow-500/60 font-medium tracking-wide">
//           Costs 100 PTS per spin
//         </p> */}

//       </main>

//       {/* Footer copyright section */}
//       {/* <footer className="relative w-full text-center py-4 z-10">
//         <p className="text-[10px] text-gray-600 font-bold uppercase tracking-widest">
//           &copy; {new Date().getFullYear()} Wheel of Fortune. All rights reserved.
//         </p>
//       </footer> */}

//       {/* Reward / Better Luck Next Time popup Modal */}
//       {showResultModal && spinResult && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-[fadeIn_0.2s_ease-out]">
//           <div className="relative w-full max-w-sm border-2 border-yellow-500/60 bg-gradient-to-b from-[#211E15] to-[#0A0905] rounded-2xl p-6 text-center shadow-2xl shadow-yellow-500/10">
            
//             {/* Glowing background rays on win */}
//             {spinResult.isWin && (
//               <div className="absolute inset-0 pointer-events-none opacity-20 bg-[radial-gradient(circle_at_center,_#D4AF37_0%,_transparent_60%)] animate-pulse" />
//             )}

//             {/* Modal Content */}
//             <div className="relative z-10">
//               {/* Icon / Emoji header */}
//               <div className="text-6xl mb-3 animate-bounce">
//                 {spinResult.icon}
//               </div>

//               <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-amber-500 tracking-wider uppercase mb-1">
//                 {spinResult.isWin ? "Congratulations!" : "Too Bad!"}
//               </h3>
              
//               <p className="text-xs text-gray-400 font-semibold tracking-wider uppercase mb-4">
//                 {spinResult.isWin ? "You landed on" : "No luck this time"}
//               </p>

//               <div className="bg-black/60 border border-yellow-500/20 py-4 px-3 rounded-lg mb-6">
//                 <span className="block text-3xl font-black text-yellow-400 tracking-wide">
//                   {spinResult.label}
//                 </span>
//                 {spinResult.subLabel && (
//                   <span className="block text-xs font-bold text-gray-300 uppercase tracking-widest mt-1">
//                     {spinResult.subLabel}
//                   </span>
//                 )}
//               </div>

//               {spinResult.isWin ? (
//                 <p className="text-sm text-yellow-500/80 font-medium mb-6">
//                   {spinResult.value.toLocaleString()} points added to your balance!
//                 </p>
//               ) : (
//                 <p className="text-sm text-gray-500 font-medium mb-6">
//                   Don't lose hope. Try your luck again!
//                 </p>
//               )}

//               {/* Action Buttons */}
//               <div className="flex gap-3 justify-center">
//                 <button
//                   onClick={() => setShowResultModal(false)}
//                   className="px-6 py-2 rounded-lg border border-yellow-500/40 bg-gradient-to-b from-yellow-500/20 to-yellow-600/10 hover:from-yellow-500/30 hover:to-yellow-600/20 text-yellow-400 font-bold text-sm tracking-wider uppercase transition-all shadow-md active:scale-95 cursor-pointer"
//                 >
//                   Spin Again
//                 </button>
//                 <Link
//                   to="/home"
//                   className="px-6 py-2 rounded-lg border border-gray-600/40 bg-gray-900/60 hover:bg-gray-800 text-gray-300 font-bold text-sm tracking-wider uppercase transition-all shadow-md active:scale-95"
//                 >
//                   Go Home
//                 </Link>
//               </div>
//             </div>

//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default GameScene;
